import { createFileRoute, Link } from "@tanstack/react-router";
import { ShieldCheck, Building2, Users, KeyRound, ArrowRight, Globe, FileSignature, Server } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { alternateLinkList } from "@/lib/i18n/locale";
import hero from "@/assets/nfc-card-trio.jpg";
import sso from "@/assets/ai-abstract.jpg";
import brand from "@/assets/cards-design.webp";
import security from "@/assets/cards-hero.webp";

export const Route = createFileRoute("/el/solutions/enterprise")({
  head: () => ({
    meta: [
      { title: "Επαγγελματικές κάρτες NFC & CRM για μεγάλες επιχειρήσεις — banana.cards" },
      { name: "description", content: "SSO, SCIM, SOC 2, χρέωση ανά χρήστη και αποκλειστική ομάδα υποστήριξης επιτυχίας. Διαθέστε εταιρικές κάρτες και CRM με AI σε 10.000+ εργαζομένους σε ημέρες, όχι τρίμηνα." },
      { property: "og:title", content: "Επαγγελματικές κάρτες NFC & CRM για μεγάλες επιχειρήσεις — banana.cards" },
      { property: "og:description", content: "SSO, SCIM, SOC 2, αποκλειστική υποστήριξη, SLA 99,9%." },
      { property: "og:image", content: hero },
      { property: "og:locale", content: "el_GR" },
    ],
    links: alternateLinkList("/solutions/enterprise", "el"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Για μεγάλες επιχειρήσεις"
      title="Σχεδιασμένο για"
      italic="μεγάλη κλίμακα."
      description="SAML SSO, παροχή λογαριασμών SCIM, αρχεία ελέγχου, προσαρμοσμένο DPA και SLA 99,9% — γύρω από την ίδια πλατφόρμα Banana που αγαπούν ήδη οι εργαζόμενοί σας."
      heroImage={hero}
      primaryCta={{ label: "Επικοινωνήστε με τις πωλήσεις", to: "/el/contact" }}
      secondaryCta={{ label: "Λήψη πακέτου ασφάλειας", to: "/security" }}
      metrics={[
        { v: "10k+", l: "Λογαριασμοί σε 24 ώρες" },
        { v: "99.9%", l: "SLA διαθεσιμότητας" },
        { v: "SOC 2", l: "Πιστοποίηση Type II" },
      ]}
      bullets={[
        { icon: KeyRound, title: "SSO, SCIM & RBAC", desc: "Okta, Azure AD, Google Workspace και κάθε πάροχος ταυτότητας SAML 2.0. Παροχή λογαριασμών JIT, ρόλοι ανά ομάδα και απενεργοποίηση με ένα κλικ." },
        { icon: Building2, title: "Εταιρική ταυτότητα σε κάθε επίπεδο", desc: "Πρότυπα καρτών για όλο τον οργανισμό, επιμέρους επωνυμίες ανά περιοχή, προσαρμοσμένα domain, προφίλ white-label και κλειδωμένες παράμετροι σχεδιασμού για την ομάδα σας." },
        { icon: ShieldCheck, title: "Ενσωματωμένη ασφάλεια & συμμόρφωση", desc: "SOC 2 Type II, ISO 27001, GDPR, CCPA. Προσαρμοσμένο DPA, διαμονή δεδομένων σε EU/US/APAC και ροή αρχείων ελέγχου στο SIEM σας." },
      ]}
      deepDives={[
        {
          eyebrow: "Ταυτότητα",
          title: "Δημιουργήστε 10.000 κάρτες",
          italic: "σε ένα απόγευμα.",
          description: "Συγχρονίστε τον κατάλογό σας μέσω SCIM. Το Banana δημιουργεί ψηφιακά προφίλ με εταιρική ταυτότητα, στέλνει φυσικές κάρτες NFC στο σπίτι κάθε εργαζομένου και ανακαλεί την πρόσβαση μόλις το HR καταχωρίσει την αποχώρηση.",
          points: [
            "SAML 2.0 SSO (Okta, Azure AD, Google, Ping, OneLogin)",
            "Παροχή λογαριασμών JIT με SCIM 2.0",
            "Ρόλοι ανά ομάδα & εύρος δικαιωμάτων",
            "Μαζική αποστολή καρτών σε κατοικίες παγκοσμίως",
          ],
          image: sso,
        },
        {
          eyebrow: "Εταιρική ταυτότητα",
          title: "Η επωνυμία σας σε κάθε",
          italic: "άγγιγμα, σάρωση και υπογραφή.",
          description: "Σχεδιάστε μία φορά στα κεντρικά, κλειδώστε το σύστημα και διαθέστε το στις τοπικές ομάδες. Πρότυπα, γραμματοσειρές, χρώματα, διατάξεις και εγκεκριμένες εικόνες — με έλεγχο, εκδόσεις και άμεση εφαρμογή.",
          points: [
            "Βιβλιοθήκη προτύπων οργανισμού & επιμέρους επωνυμιών",
            "Προσαρμοσμένα domain & προφίλ white-label",
            "Εγκεκριμένες εικόνες & κλείδωμα γραμματοσειρών",
            "Εφαρμογή υπογραφών email σε όλους τους χρήστες",
          ],
          image: brand,
          reverse: true,
        },
        {
          eyebrow: "Ασφάλεια",
          title: "Όσα θέλει να διαβάσει",
          italic: "ο CISO σας.",
          description: "SOC 2 Type II, ISO 27001, δοκιμές διείσδυσης, πρόγραμμα γνωστοποίησης ευπαθειών και κρυπτογράφηση σημειώσεων από άκρο σε άκρο. Επιλέξτε διαμονή δεδομένων σε EU, US ή APAC. Διατίθενται προσαρμοσμένα DPA και BAA.",
          points: [
            "SOC 2 Type II & ISO 27001",
            "Διαμονή δεδομένων EU / US / APAC",
            "Ροή αρχείων ελέγχου (Splunk, Datadog, S3)",
            "Διαθέσιμα προσαρμοσμένα DPA, BAA & MSA",
          ],
          image: security,
        },
      ]}
      howItWorks={[
        { title: "Έναρξη & καθορισμός πεδίου", desc: "Αποκλειστικός μηχανικός λύσεων χαρτογραφεί τον πάροχο ταυτότητας, την εταιρική ταυτότητα, τις περιοχές και τα στάδια υλοποίησης." },
        { title: "Πιλοτική λειτουργία σε 2 εβδομάδες", desc: "Πιλοτική εφαρμογή για 100 χρήστες με πλήρες SSO, παράδοση εταιρικών καρτών και ενεργές ενσωματώσεις CRM." },
        { title: "Παγκόσμια διάθεση", desc: "Η παροχή λογαριασμών SCIM και η μαζική αποστολή καρτών καλύπτουν χιλιάδες χρήστες σε διαφορετικές περιοχές μέσα σε ημέρες." },
        { title: "Μακροπρόθεσμη επιτυχία", desc: "Αποκλειστικός CSM, τριμηνιαίες επιχειρηματικές ανασκοπήσεις, εταιρική υποστήριξη 24/7 και συμμετοχή στον σχεδιασμό εξέλιξης." },
      ]}
      capabilities={[
        "SAML 2.0 SSO & παροχή λογαριασμών SCIM",
        "Έλεγχος πρόσβασης βάσει ρόλων",
        "SOC 2 Type II & ISO 27001",
        "Προσαρμοσμένα DPA, BAA & MSA",
        "Διαμονή δεδομένων EU / US / APAC",
        "Ροή αρχείων ελέγχου στο SIEM",
        "Συγχρονισμός Salesforce, HubSpot & Workday",
        "Αποκλειστικός CSM & υποστήριξη 24/7",
        "SLA διαθεσιμότητας 99.9%",
        "Προσαρμοσμένα domain & white-label",
      ]}
      faq={[
        { q: "Πώς λειτουργούν τα SSO και SCIM;", a: "Υποστηρίζουμε κάθε πάροχο ταυτότητας SAML 2.0 — Okta, Azure AD, Google Workspace, Ping, OneLogin, Duo και άλλους. Το SCIM 2.0 χειρίζεται την παροχή λογαριασμών JIT: οι νέοι εργαζόμενοι αποκτούν προφίλ και δρομολογείται η κάρτα τους την ημέρα που τους προσθέτει το HR. Η αποχώρηση ανακαλεί αμέσως την πρόσβαση σε web, κινητό και φυσικό chip NFC." },
        { q: "Πού αποθηκεύονται τα δεδομένα μας;", a: "Εσείς επιλέγετε. Το Banana λειτουργεί περιφερειακές υποδομές δεδομένων σε EU (Φρανκφούρτη), US (Βιρτζίνια) και APAC (Σιγκαπούρη). Όλα τα δεδομένα — προφίλ, σημειώσεις, εγγραφές — παραμένουν στην επιλεγμένη περιοχή. Η αντιγραφή μεταξύ περιοχών είναι προαιρετική και απενεργοποιημένη από προεπιλογή για ρυθμιζόμενους κλάδους." },
        { q: "Ποιες πιστοποιήσεις συμμόρφωσης διαθέτετε;", a: "SOC 2 Type II, ISO 27001 και συμμόρφωση με GDPR και CCPA. Υποστήριξη HIPAA με υπογεγραμμένο BAA στο Enterprise. Ετήσιες δοκιμές διείσδυσης από τρίτους, δημόσιο πρόγραμμα γνωστοποίησης ευπαθειών και κέντρο εμπιστοσύνης με ενημερωμένες αναφορές." },
        { q: "Μπορούμε να διαθέσουμε το προϊόν με τη δική μας επωνυμία;", a: "Ναι. Προσαρμοσμένα domain, αφαίρεση της επωνυμίας Banana, το λογότυπο και τα χρώματά σας παντού — σελίδες προφίλ, email, κάρτες wallet και υπογραφές. Το Enterprise περιλαμβάνει εφαρμογές κινητού με κοινή επωνυμία για τις μεγαλύτερες υλοποιήσεις." },
        { q: "Πώς διαμορφώνεται η τιμή για 10.000 χρήστες;", a: "Η χρέωση γίνεται ανά χρήστη, με κλίμακες όγκου. Περιλαμβάνονται απεριόριστες κάρτες — για φυσικές αντικαταστάσεις πληρώνετε μόνο υλικά και μεταφορικά — απεριόριστο AI με πολιτική εύλογης χρήσης, όλες οι ενσωματώσεις και αποκλειστική υποστήριξη επιτυχίας. Μια τυπική υλοποίηση 10.000 χρηστών κοστίζει σημαντικά λιγότερο από έναν διαχειριστή CRM πλήρους απασχόλησης." },
        { q: "Πόσο γρήγορα μπορούμε να ξεκινήσουμε;", a: "Πιλοτική λειτουργία 100 χρηστών σε 2 εβδομάδες. Η πλήρης υλοποίηση για 10.000+ χρήστες συνήθως απαιτεί 4–6 εβδομάδες, μαζί με προμήθειες, ρύθμιση SSO, έγκριση εταιρικών προτύπων και μαζική αποστολή καρτών σε κατοικίες παγκοσμίως." },
      ]}
      outro={{
        title: "Έτοιμοι όταν",
        italic: "είστε κι εσείς.",
        description: "Ζητήστε παρουσίαση, πακέτο ασφάλειας και εξατομικευμένη πρόταση τιμολόγησης με βάση τους χρήστες και τις περιοχές σας.",
      }}
      afterFaq={<EntUseCases />}
    />
  );
}

function EntUseCases() {
  const items = [
    { i: Globe, t: "Παγκόσμια διάθεση", d: "Υποδομές δεδομένων σε πολλές περιοχές, μαζική αποστολή καρτών σε 80+ χώρες και προφίλ προσαρμοσμένα στη γλώσσα.", to: "/el/platform" },
    { i: Users, t: "Μεγάλοι οργανισμοί πωλήσεων", d: "Πρότυπα ανά ομάδα, πίνακες ελέγχου υπευθύνων και αμφίδρομος συγχρονισμός Salesforce & HubSpot.", to: "/el/solutions/sales" },
    { i: FileSignature, t: "Ρυθμιζόμενοι κλάδοι", d: "HIPAA BAA, αρχεία ελέγχου, υπογεγραμμένες συμβάσεις και έλεγχος διαμονής δεδομένων.", to: "/security" },
    { i: Server, t: "Ομάδες IT & ασφάλειας", d: "SSO, SCIM, ροή SIEM, ενιαίο κέντρο εμπιστοσύνης και καθορισμένος υπεύθυνος ασφάλειας.", to: "/security" },
  ];
  return (
    <section className="bg-background">
      <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Εφαρμογές για μεγάλες επιχειρήσεις</p>
        <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
          Σχεδιασμένο για τον τρόπο που <em className="italic">αγοράζουν πραγματικά οι μεγάλοι οργανισμοί.</em>
        </h2>
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {items.map((i) => {
            const Icon = i.i;
            return (
              <Link key={i.t} to={i.to} className="group rounded-2xl bg-muted p-6 transition hover:bg-muted/70">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-soft">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-bold">{i.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{i.d}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold underline-offset-4 group-hover:underline">
                  Εξερευνήστε <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}